import React, { useContext } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import OrthoticsBanner from '../pics/PODBANNER.jpg';
import COL from '../Components/2-col-section-PH';
import { DataContext } from '../Components/DataContext';
import WavyLine from '../Components/wavyline';
import SEOHead from '../Components/SEOHead';
import PageBreadcrumbs from '../Components/PageBreadcrumbs';
import { FaCheckCircle, FaArrowRight, FaPhone, FaUserMd, FaHeart, FaShieldAlt, FaClock } from 'react-icons/fa';

function OrthoticsPage() {
  const data = useContext(DataContext);

  if (!data || !data.OrthoticsInfo || !data.OrthoticsPage) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-secondary"></div>
      </div>
    );
  }

  const { OrthoticsInfo } = data;
  const { OrthTitle, OrthDesc } = OrthoticsInfo[0];
  const { hero, benefits, process, callToAction } = data.OrthoticsPage;

  // SEO structured data for Orthotics page
  const orthoticsPageStructuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Custom Orthotics Manchester - Drosh The Pod",
    "description": "Professional custom orthotics services in Manchester. Expert assessment, design, and fitting of orthotic insoles for foot pain relief and improved mobility.",
    "url": "https://www.droshpod.com/orthotics",
    "provider": {
      "@type": "Organization",
      "name": "Drosh The Pod",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Manchester",
        "addressRegion": "Greater Manchester",
        "addressCountry": "GB"
      }
    },
    "serviceType": "Custom Orthotics",
    "areaServed": "Manchester",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Orthotics Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Orthotic Assessment"
          }
        },
        {
          "@type": "Offer", 
          "itemOffered": {
            "@type": "Service",
            "name": "Custom Orthotic Design"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service", 
            "name": "Orthotic Fitting"
          }
        }
      ]
    }
  };

  return (
    <>
      <SEOHead 
        title="Custom Orthotics Manchester | Professional Solutions"
        description="Professional custom orthotics services in Manchester. Expert assessment, design, and fitting of orthotic insoles for foot pain relief and improved mobility."
        keywords="custom orthotics manchester, orthotic insoles, foot orthotics, podiatry orthotics, manchester orthotics, foot pain relief"
        canonical="https://www.droshpod.com/orthotics"
        structuredData={orthoticsPageStructuredData}
      />
      
      <main className="min-h-screen">
        {/* Breadcrumbs */}
        <div className="container mx-auto px-4 pt-6">
          <PageBreadcrumbs />
        </div>

        {/* Hero Section */}
        <section className="relative">
          <div className="absolute inset-0 top-1/2 overflow-hidden -z-10">
            <WavyLine />
          </div>
          
          <div className="container mx-auto px-4 py-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ x: -50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.8 }}
                className="order-2 lg:order-1"
              >
                <h1 className="text-4xl md:text-6xl font-bold text-gray-800 mb-6">
                  {hero.title}
                </h1>
                <p className="text-xl text-gray-600 mb-6">
                  {hero.subtitle}
                </p>
                <p className="text-lg text-gray-500 leading-relaxed mb-8">
                  {hero.description}
                </p>
                <Link 
                  to="/consultation-form"
                  className="bg-secondary text-white px-8 py-3 rounded-full font-semibold hover:bg-primary transition-colors duration-300 inline-flex items-center"
                >
                  Book Consultation
                  <FaArrowRight className="ml-2" />
                </Link>
              </motion.div>
              
              <motion.div
                initial={{ x: 50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="order-1 lg:order-2"
              >
                <div className="relative">
                  <img 
                    src={OrthoticsBanner} 
                    alt="Custom Orthotics Manchester - Professional Podiatry Treatment" 
                    className="w-full h-96 object-cover rounded-2xl shadow-xl"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl"></div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                {benefits.title}
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Custom orthotics can provide significant relief and improve your overall foot health and mobility.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {benefits.items.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ y: 50, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="flex items-start">
                    <FaCheckCircle className="text-green-500 text-xl mr-3 mt-1 flex-shrink-0" />
                    <p className="text-gray-700 font-medium">{benefit}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                {process.title}
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Our comprehensive approach ensures you get the perfect orthotic solution for your needs.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
              {process.steps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ y: 50, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center"
                >
                  <div className="bg-primary/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-secondary font-bold text-xl">{index + 1}</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-gray-600">
                    {step.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Why Choose Our Orthotics Service?
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Experience the difference that professional orthotic care can make to your foot health.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
              <motion.div
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
                className="text-center p-6 rounded-lg hover:shadow-lg transition-shadow duration-300"
              >
                <FaUserMd className="text-4xl text-secondary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Expert Assessment</h3>
                <p className="text-gray-600">Comprehensive foot examination and gait analysis by qualified podiatrists.</p>
              </motion.div>

              <motion.div
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="text-center p-6 rounded-lg hover:shadow-lg transition-shadow duration-300"
              >
                <FaHeart className="text-4xl text-red-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Custom Design</h3>
                <p className="text-gray-600">Tailored orthotic design based on your specific foot structure and needs.</p>
              </motion.div>

              <motion.div
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
                className="text-center p-6 rounded-lg hover:shadow-lg transition-shadow duration-300"
              >
                <FaShieldAlt className="text-4xl text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Quality Materials</h3>
                <p className="text-gray-600">Premium materials and advanced technology for lasting comfort and support.</p>
              </motion.div>

              <motion.div
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
                className="text-center p-6 rounded-lg hover:shadow-lg transition-shadow duration-300"
              >
                <FaClock className="text-4xl text-purple-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Ongoing Support</h3>
                <p className="text-gray-600">Regular follow-up appointments to ensure optimal fit and effectiveness.</p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Original Content Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <COL
              title={OrthTitle}
              description={OrthDesc} 
              imgSrc={OrthoticsBanner}
              imgRight={false}
            />
          </div>
        </section>

        {/* Call to Action Section */}
        <section className="py-16 bg-gradient-to-r from-secondary to-primary text-white">
          <div className="container mx-auto px-4 text-center">
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="max-w-3xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {callToAction.title}
              </h2>
              <p className="text-xl mb-8 opacity-90">
                {callToAction.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to={callToAction.buttonLink}
                  className="bg-white text-secondary px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors duration-300"
                >
                  {callToAction.buttonText}
                </Link>
                <a 
                  href="tel:07809267586"
                  className="border-2 border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-secondary transition-colors duration-300 flex items-center justify-center"
                >
                  <FaPhone className="mr-2" />
                  Call Us
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
    </>
  );
}

export default OrthoticsPage;
